/*
 * OPUS 名刺注文フォーム
 * - 本体（名刺印刷：仕上げ × 印刷面 × 数量区分、価格は1枚あたり）を枚数分カートに入れ、
 *   デザイン作成・郵送料を「子（ネスト）行」として追加します。
 * - 価格はすべてShopifyのバリエーション価格から計算するので、表示とカートの金額が一致します。
 * - 画面の作りは Tシャツ注文ページ（opus-tshirt.css）と共通です。
 * - 用語：こだわり（外注・紙質を選べる）／おためし（自社の店内印刷・即日）
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-opus-meishi]');
  if (!root) return;
  const mount = root.querySelector('[data-opus-builder]');
  let cfg;
  try {
    cfg = JSON.parse(root.querySelector('[data-opus-config]').textContent);
  } catch (e) {
    mount.innerHTML = '<p class="tb-err">注文フォームの読み込みに失敗しました。ページを再読み込みしてください。</p>';
    return;
  }

  /* ---------- 小さな道具 ---------- */
  const P = cfg.products || {};
  const SET_KEY = 'セット番号';
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const lines = (arr) => (arr || []).map((s) => String(s).replace(/<br\s*\/?>/gi, '').trim()).filter(Boolean);
  const fmt = (cents) => {
    const a = (cents || 0) / 100;
    const f = cfg.moneyFormat || '¥{{amount_no_decimals}}';
    return f
      .replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) =>
        /no_decimals/.test(k)
          ? Math.round(a).toLocaleString('ja-JP')
          : a.toLocaleString('ja-JP', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      )
      .replace(/<[^>]*>/g, '');
  };
  const fileSize = (b) => (b >= 1048576 ? (b / 1048576).toFixed(1) + 'MB' : Math.max(1, Math.round(b / 1024)) + 'KB');
  const fileExt = (name) => (name.split('.').pop() || '').toUpperCase().slice(0, 4);
  const acceptList = (cfg.accept || '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
  const acceptLabel = acceptList.map((s) => s.replace('.', '').toUpperCase()).filter((s) => s !== 'JPEG').join('・');
  const nums = (arr) => (arr || []).map((x) => parseInt(x, 10)).filter((n) => n > 0);

  /** 納期の文字から営業日数を読む（「3営業日」→3、「翌営業日」→1、「即日」→0） */
  const leadDaysOf = (text, fallback) => {
    const t = String(text || '');
    const m = t.match(/(\d+)\s*営業日/);
    if (m) return +m[1];
    if (/翌営業日|翌日/.test(t)) return 1;
    if (/即日|当日/.test(t)) return 0;
    return fallback;
  };

  /* ---------- 商品・バリエーション ---------- */
  const RE_TIER = /数量|枚数|区分|tier/i;
  const RE_SIDE = /印刷面|面|side/i;
  const RE_FINISH = /仕上|用紙|紙|方法|コース|finish|paper/i;
  const optIndex = (prod, re, skip) => (prod ? prod.options.findIndex((n, i) => i !== skip && re.test(n)) : -1);

  const main = P.main;
  const ti = optIndex(main, RE_TIER);
  const si = optIndex(main, RE_SIDE, ti);
  const fi = optIndex(main, RE_FINISH, ti) === si ? -1 : optIndex(main, RE_FINISH, ti);

  /**
   * 数量区分の値を選ぶ。値の先頭の数字（「100枚以上」なら100）が区分の開始枚数と一致するものを使うので、
   * Shopify側の値の並び順が違っていても正しく選べる。見つからないときは並び順で選ぶ。
   */
  function tierValue(vals, tier) {
    const start = tierMins[tier];
    const hit = vals.find((x) => {
      const m = String(x).replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xfee0)).match(/\d+/);
      return m && +m[0] === start;
    });
    return hit != null ? hit : vals[Math.min(tier, vals.length - 1)];
  }

  /** 商品からバリエーションを選ぶ。want は {オプション位置: 値}、tier は数量区分の番号 */
  function pick(prod, want, tier) {
    if (!prod) return null;
    const t = optIndex(prod, RE_TIER);
    return (
      prod.variants.find((v) =>
        prod.options.every((name, i) => {
          if (i === t && !(want && want[i] != null)) return v.options[i] === tierValue(prod.optionValues[i], tier || 0);
          if (want && want[i] != null) return v.options[i] === want[i];
          return true;
        })
      ) || null
    );
  }

  const tierMins = nums(cfg.tierMins);
  if (!tierMins.length) tierMins.push(10);
  const tierOf = (n) => tierMins.reduce((t, m, i) => (n >= m ? i : t), 0);
  const tierLabel = (t) => (tierMins[t + 1] ? `${tierMins[t]}〜${tierMins[t + 1] - 1}枚` : `${tierMins[t]}枚以上`);
  const step = Math.max(1, cfg.qtyStep || 10);

  // 仕上げ（例：「おためし・上質マット紙」「こだわり・アートポスト・厚手」）→ コースと紙質に分ける
  const inPre = (cfg.inhousePrefix || 'おためし').trim();
  const finishes = main && fi >= 0 ? main.optionValues[fi] : [];
  const methodOf = (f) => (f.indexOf(inPre) === 0 ? 'inhouse' : 'factory');
  const paperOf = (f) => {
    const i = f.search(/[・:：]/);
    return (i >= 0 ? f.slice(i + 1) : f).trim();
  };
  const finishesOf = (m) => finishes.filter((f) => methodOf(f) === m);
  const methods = ['factory', 'inhouse'].filter((m) => finishesOf(m).length);
  const sides = main && si >= 0 ? main.optionValues[si] : [];
  const isDouble = (s) => /両/.test(s);
  const paperNotes = {};
  lines(cfg.paperNotes).forEach((l) => {
    const m = l.match(/^(.+?)\s*[:：]\s*(.+)$/);
    if (m) paperNotes[m[1].trim()] = m[2].trim();
  });

  const noteFor = (p) => paperNotes[p] || (Object.keys(paperNotes).find((k) => p.indexOf(k) === 0) ? paperNotes[Object.keys(paperNotes).find((k) => p.indexOf(k) === 0)] : '');

  // コースごとの枚数のルール
  const rule = (m) => {
    const r = (cfg.qtyRules || {})[m] || {};
    const min = Math.max(step, parseInt(r.min, 10) || tierMins[0]);
    const max = Math.max(min, parseInt(r.max, 10) || 1000);
    return { min, max, quick: nums(r.quick).filter((n) => n >= min && n <= max) };
  };
  const roundIn = (m, n) => {
    const r = rule(m);
    return Math.min(Math.floor(r.max / step) * step, Math.max(r.min, Math.ceil((parseInt(n, 10) || 0) / step) * step));
  };

  // 追加料金の商品
  const firstVariant = (prod) => (prod ? prod.variants.find((v) => v.available !== false) || null : null);
  const designVariant = firstVariant(P.design);
  const shipVariant = firstVariant(P.ship);
  const templates = (cfg.templates || []).filter((t) => t && t.name);

  // 名刺に入れる内容（入力欄）
  const FIELDS = [
    ['company', '会社名', 'organization', '例：株式会社OPUS'],
    ['dept', '部署', '', '例：営業部'],
    ['title', '役職', 'organization-title', '例：マネージャー'],
    ['name', '氏名', 'name', '例：函館 太郎（ローマ字表記もあれば併記）'],
    ['address', '住所', 'street-address', '例：〒040-0063 北海道函館市若松町1-1'],
    ['tel', '電話番号', 'tel', '例：0138-00-0000'],
    ['email', 'メールアドレス', 'email', '例：info@example.com'],
    ['sns', 'SNS', '', '例：Instagram @opus_place'],
    ['url', 'サイトURL', 'url', '例：https://opus.place'],
  ];

  /* ---------- 状態 ---------- */
  const state = {
    method: methods[0] || 'factory',
    finish: { factory: finishesOf('factory')[0] || '', inhouse: finishesOf('inhouse')[0] || '' },
    side: sides.find((s) => !isDouble(s)) || sides[0] || '',
    qty: 0,
    receive: 'pickup', // おためしの受け取り：pickup（店舗で受取）| mail（郵送）
    mode: 'upload', // upload | template | reuse | design
    template: '',
    files: { front: null, back: null, ref: null },
    urls: {},
    reuseRef: '',
    info: Object.fromEntries(FIELDS.map(([k]) => [k, ''])),
    note: '',
    agree: false,
  };
  state.qty = roundIn(state.method, 100);

  const finishNow = () => state.finish[state.method];
  const want = (finish, side) => {
    const w = {};
    if (fi >= 0) w[fi] = finish;
    if (si >= 0) w[si] = side;
    return w;
  };
  const unit = (finish, side, tier) => {
    const v = pick(main, want(finish, side), tier);
    return v ? v.price : 0;
  };

  const isInhouse = () => state.method === 'inhouse';
  const doubleNow = () => isDouble(state.side);
  const costAt = (n) => unit(finishNow(), state.side, tierOf(n)) * n;
  const mailNow = () => state.receive === 'mail';
  const leadText = () => {
    if (isInhouse()) return cfg.leadInhouse || '';
    return cfg.leadFactory || '';
  };
  const leadDays = () => {
    if (isInhouse()) return cfg.leadInhouseDays == null ? 1 : cfg.leadInhouseDays;
    return cfg.leadFactoryDays || 3;
  };
  const needsDesignWait = () => state.mode === 'design' || state.mode === 'template';
  const designSuffix = () => (needsDesignWait() && !/確定後/.test(leadText()) ? '（デザイン確定後）' : '');
  const methodTitle = (m) => (m === 'inhouse' ? cfg.inhouseTitle || 'おためし' : cfg.factoryTitle || 'こだわり');
  const templateNow = () => templates.find((t) => t.id === state.template) || null;
  const showInfo = () => state.mode === 'template' || state.mode === 'design';

  /** 今のコースで選べる価格区分（料金表の列） */
  const tiersFor = (m) => {
    const r = rule(m);
    return tierMins.map((min, t) => t).filter((t) => {
      const lo = tierMins[t];
      const hi = tierMins[t + 1] ? tierMins[t + 1] - 1 : Infinity;
      return lo <= r.max && hi >= r.min && finishesOf(m).some((f) => unit(f, sides[0], t));
    });
  };

  function calc() {
    const N = state.qty;
    const t = tierOf(N);
    const rows = [];
    const missing = [];
    const v = pick(main, want(finishNow(), state.side), t);
    if (v) rows.push({ key: 'main', label: `名刺（${methodTitle(state.method)}・${paperOf(finishNow())}・${state.side}）`, v, q: N, qtyText: `${N}枚`, total: v.price * N });
    else missing.push('名刺の価格');
    if (state.mode === 'design') {
      if (designVariant) rows.push({ key: 'design', label: 'デザイン作成', v: designVariant, q: 1, qtyText: '1式', total: designVariant.price });
      else missing.push('デザイン作成');
    }
    if (mailNow()) {
      if (shipVariant) rows.push({ key: 'ship', label: '郵送料', v: shipVariant, q: 1, qtyText: '1式', total: shipVariant.price });
      else missing.push('郵送料');
    }
    const total = rows.reduce((s, x) => s + x.total, 0);
    return { N, t, rows, total, missing, per: v ? v.price : 0 };
  }

  /** 今の枚数より、上の区分の最小枚数で頼んだほうが合計が安くなる場合に、その枚数を返す */
  function cheaperQty(N) {
    const now = costAt(N);
    const r = rule(state.method);
    let best = null;
    for (let t = tierOf(N) + 1; t < tierMins.length; t++) {
      const M = roundIn(state.method, tierMins[t]);
      if (M <= N || M > r.max) continue;
      const c = costAt(M);
      if (c <= now && (!best || c < best.cost)) best = { qty: M, cost: c, save: now - c };
    }
    return best;
  }

  /* ---------- 画面の組み立て ---------- */
  let stepNo = 0;
  const head = (title, id, meta) => `
    <div class="tb-step__head">
      <span class="tb-step__no" aria-hidden="true">${++stepNo}</span>
      <h2 class="tb-step__title" id="${id}">${title}</h2>
      <span class="tb-step__meta"${meta === null ? ' data-head-meta="' + id + '"' : ''}>${meta || ''}</span>
    </div>`;

  const radio = (name, value, checked, title, meta, extra) => `
    <label class="tb-opt">
      <input type="radio" name="${name}" value="${esc(value)}" ${checked ? 'checked' : ''} class="tb-sr">
      <span class="tb-opt__title">${title}</span>
      ${meta ? `<span class="tb-opt__meta">${meta}</span>` : ''}
      ${extra || ''}
    </label>`;

  function stepMethod() {
    if (methods.length < 2) return '';
    const cards = methods
      .map((m) => {
        const ts = tiersFor(m);
        const low = Math.min(...finishesOf(m).map((x) => unit(x, sides[0], ts[ts.length - 1] || 0)).filter(Boolean));
        const lead = m === 'inhouse' ? cfg.leadInhouse : cfg.leadFactory;
        const r = rule(m);
        return radio(
          'mc-method',
          m,
          state.method === m,
          esc(methodTitle(m)),
          `1枚 ${fmt(low)}〜・${r.min}〜${r.max}枚${lead ? `<br>${esc(lead)}` : ''}`,
          `<span class="mc-opt__text">${esc(m === 'inhouse' ? cfg.inhouseText : cfg.factoryText)}</span>`
        );
      })
      .join('');
    return `
    <section class="tb-step" aria-labelledby="mc-h-method">
      ${head('コース', 'mc-h-method')}
      <div class="tb-choice mc-choice--tall" role="radiogroup" aria-labelledby="mc-h-method">${cards}</div>
    </section>`;
  }

  function stepPaper() {
    return `
    <section class="tb-step" aria-labelledby="mc-h-paper">
      ${head('紙質', 'mc-h-paper')}
      <div data-paper></div>
    </section>`;
  }

  function renderPaper(c) {
    const box = mount.querySelector('[data-paper]');
    const list = finishesOf(state.method);
    if (list.length <= 1) {
      const p = paperOf(list[0] || '');
      box.innerHTML = `
        <div class="mc-fixed">
          <span class="mc-fixed__title">${esc(p)}</span>
          ${noteFor(p) ? `<span class="mc-fixed__text">${esc(noteFor(p))}</span>` : ''}
          <span class="mc-fixed__meta">${isInhouse() ? `${esc(methodTitle('inhouse'))}は、この紙のみです` : ''}</span>
        </div>`;
      return;
    }
    const base = unit(list[0], state.side, c.t);
    const diffs = list.map((f) => unit(f, state.side, c.t) - base);
    const same = diffs.every((d) => d === 0);
    box.innerHTML = `<div class="mc-papers mc-papers--grid" role="radiogroup" aria-labelledby="mc-h-paper">${list
      .map((f, i) => {
        const p = paperOf(f);
        const d = diffs[i];
        const meta = same ? '' : d > 0 ? `+${fmt(d)}／枚` : d < 0 ? `−${fmt(-d)}／枚` : '追加料金なし';
        return radio('mc-paper', f, f === finishNow(), esc(p), meta, noteFor(p) ? `<span class="mc-opt__text">${esc(noteFor(p))}</span>` : '');
      })
      .join('')}</div>${same ? '<p class="tb-help">どの紙も追加料金はかかりません。</p>' : ''}`;
  }

  function stepSide() {
    if (sides.length < 2) return '';
    return `
    <section class="tb-step" aria-labelledby="mc-h-side">
      ${head('印刷面', 'mc-h-side')}
      <div class="tb-choice" role="radiogroup" aria-labelledby="mc-h-side">
        ${sides.map((s) => radio('mc-side', s, s === state.side, esc(s), `<span data-side-meta="${esc(s)}"></span>`)).join('')}
      </div>
      <p class="tb-help" data-area></p>
      <p class="mc-faint" data-back-note hidden></p>
    </section>`;
  }

  function stepQty() {
    return `
    <section class="tb-step" aria-labelledby="mc-h-qty">
      ${head('枚数', 'mc-h-qty', null)}
      <div class="mc-qty">
        <div class="tb-stepper" data-stepper="qty">
          <button type="button" class="tb-stepper__btn" data-dec aria-label="${step}枚減らす">−</button>
          <input class="tb-stepper__input mc-qty__input" type="number" inputmode="numeric" step="${step}" value="${state.qty}" aria-label="枚数" aria-describedby="mc-qty-unit">
          <button type="button" class="tb-stepper__btn" data-inc aria-label="${step}枚増やす">＋</button>
        </div>
        <span class="mc-qty__unit" id="mc-qty-unit" data-qty-unit></span>
      </div>
      <div class="mc-chips" role="group" aria-label="よく選ばれる枚数" data-chips></div>
      <p class="mc-cliff" data-cliff hidden></p>
      <details class="tb-table">
        <summary>料金表を見る</summary>
        <div class="tb-table__scroll" data-table></div>
      </details>
    </section>`;
  }

  function priceTable() {
    const list = finishesOf(state.method);
    const ts = tiersFor(state.method);
    const r = rule(state.method);
    const lbl = (t) => {
      const lo = Math.max(tierMins[t], r.min);
      const hi = tierMins[t + 1] ? Math.min(tierMins[t + 1] - 1, r.max) : r.max;
      return hi >= r.max && !tierMins[t + 1] ? `${lo}〜${r.max}枚` : `${lo}〜${hi}枚`;
    };
    const ths = ts.map((t) => `<th scope="col">${esc(lbl(t))}</th>`).join('');
    // 紙ごとの価格がすべて同じなら1行にまとめる
    const sig = (f) => sides.map((s) => ts.map((t) => unit(f, s, t)).join(',')).join('|');
    const groups = [];
    list.forEach((f) => {
      const g = groups.find((x) => x.sig === sig(f));
      if (g) g.papers.push(paperOf(f));
      else groups.push({ sig: sig(f), f, papers: [paperOf(f)] });
    });
    const trs = groups
      .map((g) =>
        sides
          .map((s) => `<tr><th scope="row">${groups.length > 1 ? esc(g.papers.join('・')) + '・' : ''}${esc(s)}</th>${ts.map((t) => `<td>${fmt(unit(g.f, s, t))}</td>`).join('')}</tr>`)
          .join('')
      )
      .join('');
    const extra = [];
    if (designVariant) extra.push(`デザイン作成 ${fmt(designVariant.price)}（1注文あたり）`);
    if (shipVariant) extra.push(`郵送 +${fmt(shipVariant.price)}／店舗で受取 無料`);
    return `<table><thead><tr><th scope="col">${esc(methodTitle(state.method))}・1枚あたり（税込）</th>${ths}</tr></thead><tbody>${trs}</tbody></table>${extra
      .map((x) => `<p class="tb-help">${esc(x)}</p>`)
      .join('')}`;
  }

  function stepLead() {
    return `
    <section class="tb-step" aria-labelledby="mc-h-lead">
      ${head('納期・受け取り', 'mc-h-lead')}
      <div data-lead-box></div>
    </section>`;
  }

  function renderLead() {
    const box = mount.querySelector('[data-lead-box]');
    const waitNote = needsDesignWait() ? '<p class="tb-help">デザイン作成・テンプレートの場合は、デザインが確定してからの日数です。</p>' : '';
    const shipMeta = shipVariant ? `+${fmt(shipVariant.price)}` : '';
    box.innerHTML = `
      <div class="mc-fixed">
        <span class="mc-fixed__title">${esc(leadText()) || '担当者からご連絡します'}</span>
        <span class="mc-fixed__meta">${esc(methodTitle(state.method))}の納期は変更できません</span>
      </div>
      ${waitNote}
      <div class="tb-field">
        <span class="tb-label" id="mc-l-receive">受け取り方法</span>
        <div class="tb-choice" role="radiogroup" aria-labelledby="mc-l-receive">
          ${radio('mc-receive', 'pickup', state.receive === 'pickup', '店舗で受取', '無料', cfg.pickupNote ? `<span class="mc-opt__text">${esc(cfg.pickupNote)}</span>` : '')}
          ${radio('mc-receive', 'mail', state.receive === 'mail', '郵送', shipMeta, cfg.mailNote ? `<span class="mc-opt__text">${esc(cfg.mailNote)}</span>` : '')}
        </div>
        ${cfg.receiveNote ? `<p class="tb-help">${esc(cfg.receiveNote)}</p>` : ''}
      </div>`;
  }

  function stepData() {
    return `
    <section class="tb-step" aria-labelledby="mc-h-data">
      ${head('デザイン', 'mc-h-data')}
      <div class="mc-modes" role="radiogroup" aria-labelledby="mc-h-data" data-modes></div>
      <div data-mode-body></div>
    </section>`;
  }

  function renderModes() {
    const modes = [
      ['upload', 'データを入稿する', '無料'],
      templates.length && ['template', 'テンプレートから選ぶ', '無料'],
      ['reuse', '前回と同じデザイン', '無料'],
      designVariant && ['design', 'デザインを依頼する', `+${fmt(designVariant.price)}`],
    ].filter(Boolean);
    if (!modes.some(([v]) => v === state.mode)) state.mode = 'upload';
    mount.querySelector('[data-modes]').innerHTML = modes.map(([v, t, m]) => radio('mc-mode', v, state.mode === v, t, m)).join('');
  }

  function dropHTML(key, label, required) {
    const f = state.files[key];
    if (f) {
      const url = state.urls[key];
      const thumb = url ? `<img src="${url}" alt="">` : `<span class="tb-file__ext">${esc(fileExt(f.name))}</span>`;
      return `
        <div class="tb-file">
          <div class="tb-file__thumb">${thumb}</div>
          <div class="tb-file__meta">
            <span class="tb-file__name">${esc(f.name)}</span>
            <span class="tb-file__size">${fileSize(f.size)}</span>
          </div>
          <label class="tb-file__btn">変更<input type="file" class="tb-sr" accept="${esc(cfg.accept)}" data-file="${key}" aria-label="${esc(label)}を変更"></label>
          <button type="button" class="tb-file__btn tb-file__btn--ghost" data-clear-file="${key}" aria-label="${esc(label)}を取り消す">取消</button>
        </div>`;
    }
    return `
      <label class="tb-drop" data-drop="${key}">
        <input type="file" class="tb-sr" accept="${esc(cfg.accept)}" data-file="${key}" aria-label="${esc(label)}を選択">
        <span class="tb-drop__icon" aria-hidden="true"></span>
        <span class="tb-drop__main">${esc(label)}を選択${required ? '' : '（任意）'}</span>
        <span class="tb-drop__sub">ドラッグ＆ドロップもできます／${esc(acceptLabel)}・${cfg.maxFileMB || 20}MBまで</span>
      </label>`;
  }

  function infoHTML(required) {
    return `
      <fieldset class="mc-info">
        <legend class="tb-label">名刺に入れる内容${required ? '' : '<b>（決まっている分だけで構いません）</b>'}</legend>
        <div class="mc-info__grid">
          ${FIELDS.map(([k, label, ac, ph]) => `
            <label class="tb-field mc-info__f mc-info__f--${k}">
              <span class="mc-info__label">${esc(label)}${required && k === 'name' ? '<em>必須</em>' : ''}</span>
              <input class="tb-input" type="${k === 'email' ? 'email' : k === 'tel' ? 'tel' : k === 'url' ? 'url' : 'text'}" data-info="${k}" value="${esc(state.info[k])}" placeholder="${esc(ph)}"${ac ? ` autocomplete="${ac}"` : ''}>
            </label>`).join('')}
        </div>
        <p class="tb-err" data-err="info" hidden></p>
        <p class="tb-help">入れない項目は空欄のままで構いません。</p>
      </fieldset>`;
  }

  function renderMode() {
    const box = mount.querySelector('[data-mode-body]');
    const area = (back) => (back ? (isInhouse() ? cfg.areaBackInhouse : cfg.areaBackFactory) : cfg.areaFront);
    let html = '';
    if (state.mode === 'upload') {
      html = `
        <div class="mc-files">
          <div class="mc-file">
            <div class="tb-loc__head"><span class="tb-loc__title">表面</span><span class="tb-loc__price">${esc(area(false))}</span></div>
            ${dropHTML('front', '表面のデータ', true)}
            <p class="tb-err" data-err="front" hidden></p>
          </div>
          ${doubleNow() ? `
          <div class="mc-file">
            <div class="tb-loc__head"><span class="tb-loc__title">裏面</span><span class="tb-loc__price">${esc(area(true))}</span></div>
            ${dropHTML('back', '裏面のデータ', true)}
            <p class="tb-err" data-err="back" hidden></p>
          </div>` : ''}
        </div>
        ${cfg.dataNote ? `<p class="tb-note">${esc(cfg.dataNote)}</p>` : ''}`;
    } else if (state.mode === 'template') {
      html = `
        <div class="mc-tpls" role="radiogroup" aria-label="テンプレート">
          ${templates.map((t) => `
            <label class="mc-tpl">
              <input type="radio" name="mc-tpl" value="${esc(t.id)}" ${t.id === state.template ? 'checked' : ''} class="tb-sr">
              <span class="mc-tpl__img">${t.front ? `<img src="${esc(t.front)}" alt="" loading="lazy">` : ''}</span>
              <span class="mc-tpl__name">${esc(t.name)}</span>
              ${t.note ? `<span class="mc-tpl__note">${esc(t.note)}</span>` : ''}
            </label>`).join('')}
        </div>
        <p class="tb-err" data-err="template" hidden></p>
        ${cfg.templateNote ? `<p class="tb-help">${esc(cfg.templateNote)}</p>` : ''}
        ${infoHTML(true)}`;
    } else if (state.mode === 'reuse') {
      html = `
        ${cfg.reuseNote ? `<p class="tb-note">${esc(cfg.reuseNote)}</p>` : ''}
        <label class="tb-field">
          <span class="tb-label">前回のご注文（注文番号・時期・お名前など）</span>
          <input class="tb-input" type="text" data-reuse value="${esc(state.reuseRef)}" placeholder="例：#1024／2026年5月ごろ／函館 太郎">
        </label>
        <p class="tb-err" data-err="reuse" hidden></p>`;
    } else {
      html = `
        ${cfg.designHelp ? `<p class="tb-note">${esc(cfg.designHelp)}</p>` : ''}
        ${infoHTML(false)}
        <div class="mc-file">
          <div class="tb-loc__head"><span class="tb-loc__title">ロゴ・写真・参考にしたい名刺</span></div>
          ${dropHTML('ref', 'ファイル', false)}
          <p class="tb-err" data-err="ref" hidden></p>
        </div>`;
    }
    box.innerHTML = html;
  }

  function stepSummary() {
    return `
    <section class="tb-step tb-summary" aria-labelledby="mc-h-sum" data-summary>
      ${head('ご注文内容', 'mc-h-sum')}
      <dl class="tb-lines" data-lines></dl>
      <div class="tb-total">
        <span>合計<small>（税込）</small></span>
        <strong data-total aria-live="polite"></strong>
      </div>
      <p class="tb-per" data-per></p>
      <p class="tb-nudge" data-nudge hidden></p>
      <p class="tb-lead" data-lead hidden></p>
      <label class="tb-field">
        <span class="tb-label">備考（任意）</span>
        <textarea class="tb-input" rows="3" data-note placeholder="受け取り希望日など">${esc(state.note)}</textarea>
      </label>
      ${cfg.multiNote ? `<p class="tb-help">${esc(cfg.multiNote)}</p>` : ''}
      ${cfg.agreeText ? `
      <label class="tb-check tb-check--agree">
        <input type="checkbox" data-agree ${state.agree ? 'checked' : ''} class="tb-sr">
        <span class="tb-check__box" aria-hidden="true"></span>
        <span class="tb-check__body"><span class="tb-check__text">${esc(cfg.agreeText)}</span></span>
      </label>
      <p class="tb-err" data-err="agree" hidden></p>` : ''}
      <div class="tb-err tb-err--box" data-err="global" role="alert" hidden></div>
      <button type="button" class="opus-btn opus-btn--solid tb-cta" data-submit>カートに追加する</button>
      <p class="tb-help tb-center">ご注文後、内容を確認してから印刷に入ります。</p>
    </section>`;
  }

  function adminNotice() {
    const need = [];
    if (fi < 0 || si < 0 || ti < 0) need.push('この商品のオプションを「仕上げ」「印刷面」「数量区分」の3つにしてください');
    if (!finishesOf('inhouse').length) need.push(`仕上げに「${inPre}・〜」の値がないため、おためしは表示されません`);
    if (!P.design) need.push('デザイン作成の商品が未設定です（「デザインを依頼する」は表示されません）');
    if (!P.ship) need.push('郵送料の商品が未設定です（郵送が注文できません）');
    if (!templates.length) need.push('テンプレートがまだありません（「テンプレートから選ぶ」は表示されません。ブロックを追加すると表示されます）');
    if (!need.length) return '';
    return `<p class="tb-admin">【管理者向け・エディタ内のみ表示】${need.map(esc).join('／')}</p>`;
  }

  function render() {
    stepNo = 0;
    mount.innerHTML = `
      <div class="tb">
        ${stepMethod()}
        ${stepPaper()}
        ${stepSide()}
        ${stepQty()}
        ${stepLead()}
        ${stepData()}
        ${stepSummary()}
      </div>
      <div class="tb-bar" data-bar>
        <div class="tb-bar__sum"><span class="tb-bar__label">合計（税込）</span><strong data-bar-total></strong><span class="tb-bar__sub" data-bar-sub></span></div>
        <button type="button" class="opus-btn opus-btn--solid tb-bar__btn" data-goto-summary>内容を確認</button>
      </div>
      ${cfg.designMode ? adminNotice() : ''}`;
    renderModes();
    renderMode();
    update();
    watchSummary();
  }

  /* ---------- 表示の更新 ---------- */
  function showErr(el, msg) {
    if (!el) return;
    el.textContent = msg || '';
    el.hidden = !msg;
  }

  function update() {
    const c = calc();
    const $ = (sel) => mount.querySelector(sel);
    const r = rule(state.method);

    renderPaper(c);
    renderLead();

    // 印刷面ごとの単価・印刷範囲
    mount.querySelectorAll('[data-side-meta]').forEach((el) => {
      el.textContent = `1枚 ${fmt(unit(finishNow(), el.dataset.sideMeta, c.t))}`;
    });
    const area = $('[data-area]');
    if (area) {
      area.textContent = `印刷範囲　表面：${cfg.areaFront || ''}${doubleNow() ? `／裏面：${(isInhouse() ? cfg.areaBackInhouse : cfg.areaBackFactory) || ''}` : ''}`;
    }
    const backNote = $('[data-back-note]');
    if (backNote) {
      backNote.textContent = cfg.backNoteInhouse || '';
      backNote.hidden = !(isInhouse() && cfg.backNoteInhouse);
    }

    // 枚数
    const qi = $('.mc-qty__input');
    qi.min = r.min;
    qi.max = r.max;
    if (document.activeElement !== qi) qi.value = state.qty;
    $('[data-head-meta="mc-h-qty"]').textContent = `${r.min}〜${r.max}枚・${step}枚単位`;
    $('[data-qty-unit]').textContent = `1枚 ${fmt(c.per)}（${tierLabel(c.t)}の価格）`;
    const chips = $('[data-chips]');
    chips.innerHTML = r.quick
      .map((n) => `<button type="button" class="mc-chip${n === state.qty ? ' is-on' : ''}" data-quick="${n}" aria-pressed="${n === state.qty}">${n}枚</button>`)
      .join('');
    chips.hidden = !r.quick.length;
    $('[data-dec]').disabled = state.qty <= r.min;
    $('[data-inc]').disabled = state.qty + step > r.max;
    $('[data-table]').innerHTML = priceTable();

    // 区分の境目：上の区分のほうが合計が安いとき
    const cliff = $('[data-cliff]');
    const ch = cheaperQty(c.N);
    if (ch) {
      cliff.hidden = false;
      cliff.innerHTML = `<span><b>${ch.qty}枚</b>にすると合計 ${fmt(ch.cost)}。${ch.save > 0 ? `今より <b>${fmt(ch.save)}安く</b>なります。` : '<b>同じ金額</b>で枚数が増えます。'}</span><button type="button" class="mc-cliff__btn" data-set-qty="${ch.qty}">${ch.qty}枚にする</button>`;
    } else {
      cliff.hidden = true;
      cliff.innerHTML = '';
    }

    // 明細
    $('[data-lines]').innerHTML = c.rows
      .map((x) => `<div class="tb-line"><dt>${esc(x.label)}<span>${fmt(x.v.price)} × ${esc(x.qtyText)}</span></dt><dd>${fmt(x.total)}</dd></div>`)
      .join('');
    $('[data-total]').textContent = fmt(c.total);
    $('[data-per]').textContent = `${methodTitle(state.method)}・${c.N}枚・1枚あたり ${fmt(c.per)}`;

    const nudge = $('[data-nudge]');
    const nextMin = tierMins[c.t + 1];
    if (!ch && nextMin && nextMin <= r.max) {
      const diff = c.per - unit(finishNow(), state.side, c.t + 1);
      nudge.hidden = !(diff > 0);
      nudge.textContent = `あと${nextMin - c.N}枚で、1枚あたり${fmt(diff)}安くなります（${tierLabel(c.t + 1)}の価格）`;
    } else {
      nudge.hidden = true;
    }

    const lead = $('[data-lead]');
    const lt = leadText();
    const recv = `・${mailNow() ? '郵送' : '店舗で受取'}`;
    lead.textContent = lt ? `納期の目安：${lt}${designSuffix()}${recv}` : '';
    lead.hidden = !lt;

    $('[data-bar-total]').textContent = fmt(c.total);
    $('[data-bar-sub]').textContent = `${methodTitle(state.method)}・${c.N}枚・${state.side}`;

    // 商品名の下の「〜円」
    const from = root.querySelector('[data-opus-from]');
    if (from && main && main.variants.length) {
      const all = main.variants.map((v) => v.price).filter(Boolean);
      const firstTier = main.variants.filter((v) => ti < 0 || v.options[ti] === tierValue(main.optionValues[ti], 0)).map((v) => v.price);
      const lo = Math.min(...all);
      const lo1 = Math.min(...firstTier);
      from.innerHTML = `<strong>${fmt(lo1)}</strong>／枚〜<span>（税込・${tierMins[0]}枚から）</span>${lo < lo1 ? `<em>${esc(tierLabel(tierMins.length - 1))}なら ${fmt(lo)}／枚〜</em>` : ''}`;
    }

    // 直ったエラーは消す
    if (state.agree) showErr($('[data-err="agree"]'), '');
    if (state.files.front) showErr($('[data-err="front"]'), '');
    if (state.files.back) showErr($('[data-err="back"]'), '');
    if (state.reuseRef.trim()) showErr($('[data-err="reuse"]'), '');
    if (state.template) showErr($('[data-err="template"]'), '');
    if (state.info.name.trim()) showErr($('[data-err="info"]'), '');
    return c;
  }

  function setFile(key, file) {
    const errEl = mount.querySelector(`[data-err="${key}"]`);
    if (file) {
      const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
      if (acceptList.length && !acceptList.includes(ext)) return showErr(errEl, `${acceptLabel}のファイルを選んでください。`);
      if (file.size > (cfg.maxFileMB || 20) * 1048576) {
        return showErr(errEl, `ファイルが大きすぎます（${fileSize(file.size)}）。${cfg.maxFileMB || 20}MB以下にするか、備考欄に「別途送付」とご記入ください。`);
      }
    }
    if (state.urls[key]) URL.revokeObjectURL(state.urls[key]);
    state.files[key] = file || null;
    state.urls[key] = file && /^image\/(png|jpe?g|gif|webp)$/.test(file.type) ? URL.createObjectURL(file) : null;
    renderMode();
    update();
  }

  /* ---------- 入力イベント ---------- */
  const setQty = (n) => {
    state.qty = roundIn(state.method, n);
    update();
  };

  mount.addEventListener('click', (e) => {
    const t = e.target;
    if (t.closest('[data-stepper]') && (t.closest('[data-inc]') || t.closest('[data-dec]'))) {
      setQty(state.qty + (t.closest('[data-inc]') ? step : -step));
      return;
    }
    const q = t.closest('[data-quick]') || t.closest('[data-set-qty]');
    if (q) {
      setQty(+(q.dataset.quick || q.dataset.setQty));
      return;
    }
    const clr = t.closest('[data-clear-file]');
    if (clr) {
      setFile(clr.dataset.clearFile, null);
      return;
    }
    if (t.closest('[data-goto-summary]')) {
      mount.querySelector('[data-summary]').scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (t.closest('[data-submit]')) submit();
  });

  mount.addEventListener('change', (e) => {
    const t = e.target;
    if (t.name === 'mc-method') {
      state.method = t.value;
      state.qty = roundIn(state.method, state.qty); // コースごとの枚数の範囲に合わせる
      renderMode(); // 裏面の印刷範囲が変わる
    } else if (t.name === 'mc-paper') state.finish[state.method] = t.value;
    else if (t.name === 'mc-side') {
      state.side = t.value;
      renderMode();
    } else if (t.name === 'mc-receive') state.receive = t.value;
    else if (t.name === 'mc-mode') {
      state.mode = t.value;
      renderMode();
    } else if (t.name === 'mc-tpl') state.template = t.value;
    else if (t.matches('[data-agree]')) state.agree = t.checked;
    else if (t.matches('[data-file]')) {
      setFile(t.dataset.file, t.files && t.files[0]);
      return;
    } else if (t.matches('.mc-qty__input')) {
      setQty(t.value);
      t.value = state.qty;
      return;
    }
    update();
  });

  mount.addEventListener('input', (e) => {
    const t = e.target;
    if (t.matches('[data-reuse]')) state.reuseRef = t.value;
    else if (t.matches('[data-info]')) state.info[t.dataset.info] = t.value;
    else if (t.matches('[data-note]')) state.note = t.value;
  });

  ['dragenter', 'dragover'].forEach((ev) =>
    mount.addEventListener(ev, (e) => {
      const d = e.target.closest('[data-drop]');
      if (!d) return;
      e.preventDefault();
      d.classList.add('is-over');
    })
  );
  ['dragleave', 'drop'].forEach((ev) =>
    mount.addEventListener(ev, (e) => {
      const d = e.target.closest('[data-drop]');
      if (!d) return;
      e.preventDefault();
      d.classList.remove('is-over');
      if (ev === 'drop' && e.dataTransfer && e.dataTransfer.files[0]) setFile(d.dataset.drop, e.dataTransfer.files[0]);
    })
  );

  /* スマホ下部の合計バー：注文内容が見えている間は隠す */
  function watchSummary() {
    const bar = mount.querySelector('[data-bar]');
    const sum = mount.querySelector('[data-summary]');
    if (!('IntersectionObserver' in window) || !bar || !sum) return;
    new IntersectionObserver((entries) => {
      entries.forEach((en) => bar.classList.toggle('is-hidden', en.isIntersecting));
    }, { rootMargin: '0px 0px -40% 0px' }).observe(sum);
  }

  /* 商品画像の切り替え */
  root.addEventListener('click', (e) => {
    const th = e.target.closest('.opus-pdp__thumb');
    if (!th) return;
    const img = root.querySelector('.opus-pdp__img');
    if (img && img.tagName === 'IMG') {
      img.removeAttribute('srcset');
      img.src = th.dataset.src;
    }
    root.querySelectorAll('.opus-pdp__thumb').forEach((b) => b.classList.toggle('is-active', b === th));
  });

  /* ---------- 注文前チェック ---------- */
  function validate(c) {
    const errs = [];
    const $ = (sel) => mount.querySelector(sel);
    const need = (key, msg) => {
      const el = $(`[data-err="${key}"]`);
      showErr(el, msg);
      errs.push(el);
    };
    if (state.mode === 'upload') {
      if (!state.files.front) need('front', '表面のデータを選んでください。');
      if (doubleNow() && !state.files.back) need('back', '裏面のデータを選んでください（裏面を印刷しない場合は「片面」を選んでください）。');
    } else if (state.mode === 'template') {
      if (!templateNow()) need('template', 'テンプレートを選んでください。');
      if (!state.info.name.trim()) need('info', '氏名を入力してください。');
    } else if (state.mode === 'reuse') {
      if (!state.reuseRef.trim()) need('reuse', '前回のご注文がわかる情報を入力してください。');
    }
    if (cfg.agreeText && !state.agree) need('agree', '確認事項に同意してください。');
    if (c.missing.length) errs.push('価格の設定が見つかりません（' + c.missing.join('、') + '）。お手数ですがお問い合わせください。');
    return errs;
  }

  function designLabel() {
    if (state.mode === 'upload') return 'データ入稿';
    if (state.mode === 'template') return `テンプレート（${templateNow() ? templateNow().name : ''}）`;
    if (state.mode === 'reuse') return '前回と同じデザイン';
    return 'デザイン作成（内容はメールで相談）';
  }

  function properties(c, ref) {
    const p = {};
    p[SET_KEY] = ref;
    p['枚数'] = `${c.N}枚`;
    p['コース'] = methodTitle(state.method);
    p['紙質'] = paperOf(finishNow());
    p['印刷面'] = state.side;
    p['納期'] = leadText() + designSuffix();
    p['受け取り方法'] = mailNow() ? '郵送' : '店舗で受取';
    p['デザイン'] = designLabel();
    if (state.mode === 'reuse') p['前回のご注文'] = state.reuseRef.trim();
    if (showInfo()) {
      FIELDS.forEach(([k, label]) => {
        const v = state.info[k].trim();
        if (v) p[label] = v;
      });
    }
    if (state.note.trim()) p['備考'] = state.note.trim();
    p['注文時の見積額'] = fmt(c.total);
    // 先頭が「_」の項目はお客様のカートには表示されず、注文データ（自動処理）にだけ残ります
    p['_商品種別'] = '名刺';
    p['_制作'] = isInhouse() ? '自社' : '外注';
    p['_納期営業日'] = String(leadDays());
    p['_受取'] = mailNow() ? '郵送' : '店舗';
    return p;
  }

  function fileEntries() {
    const out = [];
    if (state.mode === 'upload') {
      if (state.files.front) out.push(['表面データ', state.files.front]);
      if (doubleNow() && state.files.back) out.push(['裏面データ', state.files.back]);
    } else if (state.mode === 'design' && state.files.ref) {
      out.push(['参考ファイル', state.files.ref]);
    }
    return out;
  }

  const postJSON = (url, data) =>
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(data) });
  const errText = async (res) => {
    try {
      const j = await res.json();
      return j.description || j.message || '';
    } catch (e) {
      return '';
    }
  };

  async function submit() {
    const c = update();
    const gErr = mount.querySelector('[data-err="global"]');
    showErr(gErr, '');
    const errs = validate(c);
    if (errs.length) {
      const msgs = errs.filter((e) => typeof e === 'string');
      if (msgs.length) showErr(gErr, msgs.join(' '));
      const first = errs.find((e) => typeof e !== 'string') || gErr;
      first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const ref = 'M-' + Date.now().toString(36).slice(-4).toUpperCase() + Math.random().toString(36).slice(2, 4).toUpperCase();
    const props = properties(c, ref);
    const mainRow = c.rows.find((x) => x.key === 'main');
    const kids = c.rows.filter((x) => x.key !== 'main').map((x) => ({ id: x.v.id, quantity: x.q, properties: { [SET_KEY]: ref } }));
    const files = fileEntries();

    if (cfg.preview) {
      window.dispatchEvent(new CustomEvent('opus:preview-submit', { detail: { main: { id: mainRow.v.id, quantity: c.N, properties: props, files: files.map(([k, f]) => `${k}: ${f.name}`) }, children: kids, total: c.total } }));
      return;
    }

    const btn = mount.querySelector('[data-submit]');
    btn.disabled = true;
    btn.textContent = 'カートに追加しています…';
    try {
      // 1) 名刺本体：データファイルを含むので multipart で送る
      const fd = new FormData();
      fd.append('id', mainRow.v.id);
      fd.append('quantity', c.N);
      Object.entries(props).forEach(([k, v]) => fd.append(`properties[${k}]`, v));
      files.forEach(([k, f]) => fd.append(`properties[${k}]`, f, f.name));
      const r1 = await fetch(cfg.cartAddUrl, { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
      if (!r1.ok) throw new Error((await errText(r1)) || 'カートに追加できませんでした。');
      const j1 = await r1.json();
      const parent = j1.items ? j1.items[0] : j1;

      // 2) デザイン作成・郵送料：本体の子（ネスト行）として追加。対応していない場合は通常の行で追加
      if (kids.length) {
        let r2 = await postJSON(cfg.cartAddUrl, { items: kids.map((k) => Object.assign({ parent_line_key: parent.key }, k)) });
        if (!r2.ok) r2 = await postJSON(cfg.cartAddUrl, { items: kids });
        if (!r2.ok) {
          const msg = await errText(r2);
          await postJSON(cfg.cartChangeUrl, { id: parent.key, quantity: 0 });
          throw new Error(msg || 'オプションをカートに追加できませんでした。');
        }
      }
      window.location.href = cfg.cartUrl;
    } catch (err) {
      showErr(gErr, `${err.message} 時間をおいて再度お試しいただくか、お問い合わせください。`);
      btn.disabled = false;
      btn.textContent = 'カートに追加する';
    }
  }

  render();
})();